import { useEffect } from 'react';
import { StatusBar, Style } from '@capacitor/status-bar';
import { isNative } from '@/lib/platform';
import { WebviewTheme } from '@/lib/webviewTheme';

/**
 * Keeps the native iOS/Android status-bar text/icon color in sync with the
 * actually-rendered app theme.
 *
 * Source of truth is the `data-theme` attribute on <html> (written by
 * `useTheme`), NOT a store — that decouples this from the theme plumbing and
 * guarantees the bar matches what's on screen even as the toggle flips live.
 *
 * Capacitor's `Style` enum is named for the BACKGROUND, which is the opposite
 * of intuition:
 *   - `Style.Dark`  → *light* text, for *dark* backgrounds
 *   - `Style.Light` → *dark* text, for *light* backgrounds
 * So dark theme must use `Style.Dark` (white text) — using `Style.Light` there
 * paints dark text on a dark background and the bar visually disappears.
 */
export function useStatusBarEffect(): void {
  useEffect(() => {
    if (!isNative()) return;

    const root = document.documentElement;

    const apply = () => {
      const isDark = root.getAttribute('data-theme') === 'dark';
      void StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
      // Pin the native WKWebView background to the app theme too. Capacitor
      // defaults it to UIColor.systemBackground (the DEVICE appearance), so a
      // Light-theme app on a dark-mode phone shows a black band behind the
      // status bar on scroll/navigation. iOS-only; no-ops elsewhere.
      void WebviewTheme.apply({ dark: isDark }).catch(() => undefined);
    };

    apply();

    const observer = new MutationObserver(apply);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
}
